import { routing } from "@/i18n/routing";

type ApiMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
type Locale = (typeof routing.locales)[number];

export const API_DOMAIN = process.env.NEXT_PUBLIC_API_DOMAIN || "https://kinofolio.com";
const ABSOLUTE_URL_REGEX = /^https?:\/\//i;
const FALLBACK_LOCALE = routing.defaultLocale;

const buildApiUrl = (path: string, locale: Locale = FALLBACK_LOCALE) => {
	if (ABSOLUTE_URL_REGEX.test(path)) return path;

	const normalizedPath = path.startsWith("/") ? path : `/${path}`;

	if (normalizedPath.startsWith("/frontapi")) {
		return normalizedPath;
	}

	const pathWithoutApi = normalizedPath.startsWith("/api/") ? normalizedPath.replace(/^\/api/, "") : normalizedPath;
	const apiPath = `/api${pathWithoutApi}`;
	const localizedPath = `/${locale}${apiPath}`;

	return `${API_DOMAIN}${localizedPath}`;
};

/**
 * Lightweight helper to talk to our API endpoints.
 * Accepts only plain object payloads (JSON) or query params for GET.
 */
export async function apiCall<T = unknown>(
	path: string,
	data?: Record<string, unknown> | FormData | URLSearchParams,
	method: ApiMethod = "GET",
	lang: Locale = FALLBACK_LOCALE
): Promise<T> {
	let url = buildApiUrl(path, lang);
	const options: RequestInit = { method, credentials: "include" };

	if (method === "GET" && data && !(data instanceof FormData)) {
		const params =
			data instanceof URLSearchParams
				? data.toString()
				: new URLSearchParams(data as Record<string, string>).toString();
		if (params) {
			url += url.includes("?") ? `&${params}` : `?${params}`;
		}
	} else if (data instanceof FormData) {
		options.body = data;
	} else if (data && typeof data === "object") {
		const params = new URLSearchParams(data as Record<string, string>);
		options.headers = { "Content-Type": "application/x-www-form-urlencoded" };
		options.body = params.toString();
	}

	const res = await fetch(url, options);
	if (!res.ok) {
		let errorMessage = `API error: ${res.status} ${res.statusText}`;
		try {
			const data = await res.clone().json();
			if (data && typeof data === "object") {
				const message = (data as { message?: unknown }).message;
				if (typeof message === "string" && message.trim()) {
					errorMessage = message;
				}
			}
		} catch {
			try {
				const text = await res.text();
				if (text.trim()) errorMessage = text;
			} catch {
				// Keep default errorMessage.
			}
		}
		throw new Error(errorMessage);
	}

	return (await res.json()) as T;
}

export async function frontApiCall<T = unknown>(
	path: string,
	data?: Record<string, unknown>,
	method: ApiMethod = "GET",
	lang: Locale = FALLBACK_LOCALE
): Promise<T> {
	return apiCall<T>(path, data, method, lang);
}
